'use client'
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'
import {
  CachingCode,
  CodeList,
  CachingCodeGroupType,
  cachingCodeGroupObj,
  CachingCodesWithUpCdNm,
} from '../_types/codeType'
import { useRequestApi } from './useRequestApi'

type CodeContextType = {
  getCodes: (codeGroupType: CachingCodeGroupType) => CachingCode[]
  getCodesWithUpCdNm: (
    cachingCodesWithUpCdNm: CachingCodesWithUpCdNm,
  ) => CachingCode[]
  isCodeInitialized: boolean
  cachingCtpvCdList?: [{ value: string; label: string }]
}

// Context 생성
const CodeContext = createContext<CodeContextType | undefined>(undefined)

// Provider 컴포넌트
export const CodeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { sendHttpRequest } = useRequestApi()
  const [cachingCodeList, setCachingCodeList] = useState<CodeList>({})
  const [cachingCtpvCdList, setCachingCtpvCdList] =
    useState<[{ value: string; label: string }]>()
  const isCodeInitialized =
    Object.keys(cachingCodeList).length !== 0 &&
    !!cachingCtpvCdList &&
    cachingCtpvCdList.length > 0

  // 계층 구조 생성 함수
  const createHierarchy = (flatData: CachingCode[]): CachingCode[] => {
    if (!Array.isArray(flatData)) {
      console.error('flatData is not an array:', flatData)
      return []
    }

    const map = new Map<string, CachingCode & { children?: CachingCode[] }>()

    flatData.forEach((item) => {
      map.set(item.cdNm, { ...item, children: [] })
    })

    flatData.forEach((item) => {
      if (item.upCdNm && map.has(item.upCdNm)) {
        const parent = map.get(item.upCdNm)!
        parent.children!.push(map.get(item.cdNm)!)
      }
    })

    // useYn이 'Y'인 항목만 반환하고, children 속성이 비어있으면 제거
    return flatData.map((item) => {
      const hierarchicalItem = map.get(item.cdNm)!
      if (hierarchicalItem.children!.length === 0) {
        delete hierarchicalItem.children
      }
      return hierarchicalItem
    })
  }

  /**
   * 코드리스트 얻기
   */
  const getCodes = (cdGroupType: CachingCodeGroupType): CachingCode[] => {
    return createHierarchy(cachingCodeList[cdGroupType])
  }

  const getCodesWithUpCdNm = (
    cachingCodesWithUpCdNm: CachingCodesWithUpCdNm,
  ): CachingCode[] => {
    return cachingCodeList[
      `${cachingCodesWithUpCdNm.schema}_${cachingCodesWithUpCdNm.cdGroupNm}`
    ].filter((item) => item.upCdNm === cachingCodesWithUpCdNm.upCdNm)
  }
  const fetchCachingCodes = () => {
    if (Object.keys(cachingCodeList).length == 0) {
      const endpoint = '/common/code/caching'

      sendHttpRequest(
        'POST',
        endpoint,
        Object.values(cachingCodeGroupObj),
        false,
        {
          cache: 'no-store',
        },
        false,
      ).then((response) => {
        const dataObj = response.data.reduce(
          (acc: CodeList, cur: CachingCode) => {
            if (!acc[`${cur.schema}_${cur.cdGroupNm}`]) {
              acc[`${cur.schema}_${cur.cdGroupNm}`] = []
            }
            acc[`${cur.schema}_${cur.cdGroupNm}`].push(cur)
            return acc
          },
          {} as CodeList,
        )
        setCachingCodeList(dataObj)
      })
    }
  }

  const fetchCachingCtpvCds = async () => {
    try {
      // 검색 조건에 맞는 endpoint 생성
      let endpoint: string = `/pbc/ic/initCtpv`
      const response = sendHttpRequest(
        'GET',
        endpoint,
        null,
        true,
        {
          cache: 'no-store',
        },
        false,
      ).then((response) => {
        if (response && response.resultType === 'success' && response.data) {
          // 데이터 조회 성공시
          setCachingCtpvCdList(response.data)
        } else {
          setCachingCtpvCdList([{ value: '', label: '' }])
          // 데이터가 없거나 실패
        }
      })
    } catch (error) {
      // 에러시
      console.error('Error fetching data:', error)
    }
  }
  //코드리스트 패치
  useEffect(() => {
    fetchCachingCodes()
    fetchCachingCtpvCds()
  }, [])
  return (
    <CodeContext.Provider
      value={{
        isCodeInitialized,
        getCodes,
        getCodesWithUpCdNm,
        cachingCtpvCdList,
      }}
    >
      {children}
    </CodeContext.Provider>
  )
}

// Context 사용 훅
export const useCode = () => {
  const context = useContext(CodeContext)
  if (!context) {
    throw new Error('useCode must be used within an CodeProvider')
  }
  return context
}
